/**
 * Backstage Dashboard — the mobile-only home for every non-talent role.
 *
 * Rendered from dashboard.tsx when useIsMobile() is true. Desktop is left
 * entirely untouched. Reuses the exact React Query keys the desktop
 * dashboards use so the cache is shared (no duplicate fetches).
 *
 * Concept: "Backstage" — calm paper-and-ink luxury. One number leads, the
 * rest recede into a quiet rhythm of sections.
 */
import { useMemo } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  Users,
  UserPlus,
  FileText,
  DollarSign,
  Mail,
  Phone,
  CalendarDays,
  BarChart3,
  Settings,
  Receipt,
  Banknote,
  ChevronRight,
  Megaphone,
  Sparkles,
  Music,
  ArrowUpRight,
} from "lucide-react";
import type { UserProfile, Region } from "@shared/schema";
import { FitnessRingCard } from "@/components/dashboard/fitness-ring";
import { FollowupsCard } from "@/components/dashboard/followups-card";
import { greeting } from "./lib";

type UserRole = "admin" | "billing" | "marketing" | "sales_rep" | "artist" | string;

const usd0 = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);

const compactUsd = (n: number) => {
  const v = n || 0;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Math.round(v)}`;
};

function relativeTime(iso?: string | null): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  const min = Math.round(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  if (d < 7) return `${d}d ago`;
  return `${Math.round(d / 7)}w ago`;
}

const RING_COLORS = {
  leads: "#22c55e",
  clients: "#3b82f6",
  dollars: "#f59e0b",
  emails: "#8b5cf6",
  calls: "#ec4899",
};

/* ── Shared presentation primitives ───────────────────────────────── */

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground/70 mb-2">{children}</p>
  );
}

function Masthead({ label }: { label: string }) {
  const { data: profile } = useQuery<UserProfile>({ queryKey: ["/api/profile"] });
  const firstName = (profile?.firstName as string) || "";
  return (
    <header className="px-5 pt-8 pb-1">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">{label}</p>
      <h1
        className="mt-1 text-[26px] leading-tight font-semibold tracking-tight text-foreground"
        data-testid="text-dashboard-greeting"
      >
        {greeting()}{firstName ? `, ${firstName}` : ""}
      </h1>
    </header>
  );
}

function HeroStat({
  label,
  value,
  sub,
  testId,
}: {
  label: string;
  value: string;
  sub?: string;
  testId?: string;
}) {
  return (
    <div
      className="rounded-3xl border border-border bg-card p-6 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
      data-testid={testId}
    >
      <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground/70">{label}</p>
      <p className="mt-2 text-4xl font-semibold tracking-tight tabular-nums text-foreground">{value}</p>
      {sub ? <p className="mt-1.5 text-sm text-muted-foreground">{sub}</p> : null}
    </div>
  );
}

function StatTile({
  label,
  value,
  sub,
  accent,
  testId,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
  testId?: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={testId}>
      <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground/70">{label}</p>
      <p
        className={`mt-1.5 text-xl font-semibold tracking-tight tabular-nums ${
          accent ? "text-primary" : "text-foreground"
        }`}
      >
        {value}
      </p>
      {sub ? <p className="mt-0.5 text-xs text-muted-foreground truncate">{sub}</p> : null}
    </div>
  );
}

type Tile = { id: string; label: string; icon: React.ComponentType<{ className?: string }>; href: string };

function TileGrid({ tiles }: { tiles: Tile[] }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      {tiles.map((t) => {
        const Icon = t.icon;
        return (
          <Link key={t.id} href={t.href}>
            <div
              className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-border bg-card py-4 active:scale-[0.97] transition-transform"
              data-testid={`tile-${t.id}`}
            >
              <Icon className="h-6 w-6 text-foreground/80" />
              <span className="text-xs font-medium text-foreground/90">{t.label}</span>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background" data-testid="backstage-dashboard">
      {children}
      <div className="h-10" />
    </div>
  );
}

/* ── Admin ────────────────────────────────────────────────────────── */

type AdminSummary = {
  totalRevenue: number;
  collected: number;
  outstanding: number;
  activeLeads: number;
  totalClients: number;
  upcomingShows: number;
  thisMonthShows: number;
  totalInvoices: number;
  unpaidCount: number;
};
type Conversion = { rate: number };
type RecentChange = {
  id: string;
  kind: string;
  title: string;
  status?: string | null;
  actor?: string | null;
  occurredAt?: string | null;
};
type UserActivity = {
  userId: string;
  userName: string;
  leadsAdded: number;
  callsLogged: number;
  emailsLogged: number;
  notesAdded: number;
};

function labelForKind(kind: string): string {
  switch (kind) {
    case "lead_created":
      return "New lead";
    case "client_created":
      return "New client";
    case "invoice_updated":
      return "Invoice update";
    default:
      return "Update";
  }
}

const ADMIN_TILES: Tile[] = [
  { id: "leads", label: "Leads", icon: Users, href: "/leads" },
  { id: "clients", label: "Clients", icon: UserPlus, href: "/clients" },
  { id: "inbox", label: "Inbox", icon: Mail, href: "/email-hub" },
  { id: "invoices", label: "Invoices", icon: FileText, href: "/invoices" },
  { id: "billing", label: "Billing", icon: Banknote, href: "/billing" },
  { id: "schedule", label: "Schedule", icon: CalendarDays, href: "/master-schedule" },
  { id: "reports", label: "Reports", icon: BarChart3, href: "/reports" },
  { id: "settings", label: "Settings", icon: Settings, href: "/settings" },
];

function AdminMobile() {
  const { data: summary } = useQuery<AdminSummary>({ queryKey: ["/api/dashboard/summary"] });
  const { data: conversion } = useQuery<Conversion>({ queryKey: ["/api/dashboard/conversion-rate"] });
  const { data: regions = [] } = useQuery<Region[]>({ queryKey: ["/api/regions"] });
  const { data: recent = [] } = useQuery<RecentChange[]>({ queryKey: ["/api/dashboard/recent-activity"] });
  const { data: activity = [] } = useQuery<UserActivity[]>({
    queryKey: ["/api/user-activity", "month"],
    queryFn: async () => {
      const r = await fetch("/api/user-activity?period=month", { credentials: "include" });
      if (!r.ok) throw new Error("failed to load user activity");
      return r.json();
    },
  });

  const topTeam = useMemo(
    () =>
      [...activity]
        .map((a) => ({
          ...a,
          total: a.leadsAdded + a.callsLogged + a.emailsLogged + a.notesAdded,
        }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 5),
    [activity],
  );

  return (
    <Shell>
      <Masthead label="Dashboard" />

      <section className="px-5 pt-3">
        <Eyebrow>This year</Eyebrow>
        <HeroStat
          label="Booked revenue"
          value={summary ? compactUsd(summary.totalRevenue) : "—"}
          sub={summary ? `${usd0(summary.collected)} collected` : undefined}
          testId="hero-admin-revenue"
        />
      </section>

      <section className="px-5 pt-4">
        <div className="grid grid-cols-2 gap-3">
          <StatTile
            label="Outstanding"
            value={summary ? compactUsd(summary.outstanding) : "—"}
            sub={summary ? `${summary.unpaidCount} invoices` : undefined}
            testId="stat-outstanding"
          />
          <StatTile
            label="Active leads"
            value={summary ? String(summary.activeLeads) : "—"}
            sub={conversion ? `${Math.round(conversion.rate)}% conversion` : undefined}
            accent
            testId="stat-active-leads"
          />
          <StatTile
            label="Upcoming events"
            value={summary ? String(summary.upcomingShows) : "—"}
            sub={summary ? `${summary.thisMonthShows} this month` : undefined}
            testId="stat-upcoming-events"
          />
          <StatTile
            label="Clients"
            value={summary ? String(summary.totalClients) : "—"}
            sub={`${regions.length} regions`}
            testId="stat-total-clients"
          />
        </div>
      </section>

      <section className="px-5 pt-7">
        <Eyebrow>Quick access</Eyebrow>
        <TileGrid tiles={ADMIN_TILES} />
      </section>

      {topTeam.length > 0 && (
        <section className="px-5 pt-7">
          <Eyebrow>Team activity · 7 days</Eyebrow>
          <div className="overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
            {topTeam.map((u) => (
              <div key={u.userId} className="flex items-center gap-3 px-4 py-3" data-testid={`row-team-${u.userId}`}>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{u.userName}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {u.leadsAdded} leads · {u.callsLogged} calls · {u.emailsLogged} emails
                  </p>
                </div>
                <span className="text-sm font-semibold tabular-nums text-primary">{u.total}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {recent.length > 0 && (
        <section className="px-5 pt-7">
          <Eyebrow>Recently changed</Eyebrow>
          <div className="overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
            {recent.slice(0, 6).map((e) => (
              <div key={e.id} className="flex items-center gap-3 px-4 py-3" data-testid={`row-recent-${e.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{e.title}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {labelForKind(e.kind)}
                    {e.actor ? ` · ${e.actor}` : ""}
                  </p>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground/70">{relativeTime(e.occurredAt)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </Shell>
  );
}

/* ── Billing ──────────────────────────────────────────────────────── */

type BillingData = {
  totalCollected: number;
  collectedCount: number;
  outstandingAmount: number;
  outstandingCount: number;
  overdueAmount: number;
  overdueCount: number;
  lateFeesCollected: number;
  lateFeesAssessed: number;
  recentPayments: Array<{
    id: string;
    accountName: string;
    amount: number;
    datePaid: string;
    paidByName?: string | null;
  }>;
};

const BILLING_TILES: Tile[] = [
  { id: "invoices", label: "Invoices", icon: FileText, href: "/invoices" },
  { id: "billing", label: "Billing", icon: Banknote, href: "/billing" },
  { id: "clients", label: "Clients", icon: Users, href: "/clients" },
];

function BillingMobile() {
  const { data } = useQuery<BillingData>({
    queryKey: ["/api/billing-dashboard", "month"],
    queryFn: async () => {
      const r = await fetch("/api/billing-dashboard?period=month", { credentials: "include" });
      if (!r.ok) throw new Error("failed to load billing dashboard");
      return r.json();
    },
  });

  return (
    <Shell>
      <Masthead label="Billing" />

      <section className="px-5 pt-3">
        <Eyebrow>This month</Eyebrow>
        <HeroStat
          label="Collected"
          value={data ? compactUsd(data.totalCollected) : "—"}
          sub={data ? `${data.collectedCount} payments` : undefined}
          testId="hero-billing-collected"
        />
      </section>

      <section className="px-5 pt-4">
        <div className="grid grid-cols-2 gap-3">
          <StatTile
            label="Outstanding"
            value={data ? compactUsd(data.outstandingAmount) : "—"}
            sub={data ? `${data.outstandingCount} invoices` : undefined}
            testId="stat-bill-outstanding"
          />
          <StatTile
            label="Overdue"
            value={data ? compactUsd(data.overdueAmount) : "—"}
            sub={data ? `${data.overdueCount} invoices` : undefined}
            testId="stat-bill-overdue"
          />
          <StatTile
            label="Late fees"
            value={data ? compactUsd(data.lateFeesCollected) : "—"}
            sub={data ? `${usd0(data.lateFeesAssessed)} assessed` : undefined}
            accent
            testId="stat-bill-late-fees"
          />
          <StatTile
            label="Receivable"
            value={data ? compactUsd(data.outstandingAmount + data.overdueAmount) : "—"}
            sub="Open balance"
            testId="stat-bill-receivable"
          />
        </div>
      </section>

      <section className="px-5 pt-7">
        <Eyebrow>Quick access</Eyebrow>
        <TileGrid tiles={BILLING_TILES} />
      </section>

      {data && data.recentPayments.length > 0 && (
        <section className="px-5 pt-7">
          <Eyebrow>Recent payments</Eyebrow>
          <div className="overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
            {data.recentPayments.slice(0, 8).map((p) => (
              <div key={p.id} className="flex items-center gap-3 px-4 py-3" data-testid={`row-payment-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{p.accountName}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {relativeTime(p.datePaid)}
                    {p.paidByName ? ` · ${p.paidByName}` : ""}
                  </p>
                </div>
                <span className="shrink-0 text-sm font-semibold tabular-nums text-primary">
                  {usd0(p.amount)}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </Shell>
  );
}

/* ── Sales rep ────────────────────────────────────────────────────── */

type SummaryData = {
  metrics: {
    leadsCreated: { value: number; delta: number };
    clientsCreated: { value: number; delta: number };
    dollarsBooked: { value: number; delta: number };
    emailsSent: { value: number; delta: number };
    callsLogged: { value: number; delta: number };
  };
};
type SlippingLead = {
  id: string;
  leadName: string;
  status: string;
  daysSinceTouch: number;
};
type SlippingResponse = { leads: SlippingLead[] };
type EffectiveGoals = { goals: Array<{ metricKey: string; period: string; target: number }> };

const REP_TILES: Tile[] = [
  { id: "leads", label: "Leads", icon: Users, href: "/leads" },
  { id: "clients", label: "Clients", icon: UserPlus, href: "/clients" },
  { id: "invoices", label: "Invoices", icon: FileText, href: "/invoices" },
  { id: "schedule", label: "Schedule", icon: CalendarDays, href: "/master-schedule" },
  { id: "inbox", label: "Inbox", icon: Mail, href: "/email-hub" },
  { id: "reports", label: "Reports", icon: BarChart3, href: "/reports" },
];

function SalesRepMobile({ userId }: { userId: string }) {
  const tzOffset = new Date().getTimezoneOffset();
  const dateParam = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);

  const { data: summary, isLoading: summaryLoading } = useQuery<SummaryData>({
    queryKey: ["/api/rep-dashboard/summary", "month", dateParam, "all", userId, tzOffset],
    queryFn: async () => {
      const r = await fetch(
        `/api/rep-dashboard/summary?range=month&date=${dateParam}&regionId=all${userId ? `&userId=${userId}` : ""}&tzOffset=${tzOffset}`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error("failed to load summary");
      return r.json();
    },
  });

  const { data: goalsResp } = useQuery<EffectiveGoals>({
    queryKey: ["/api/goals/effective", userId],
    queryFn: async () => {
      const r = await fetch(userId ? `/api/goals/effective?userId=${userId}` : "/api/goals/effective", {
        credentials: "include",
      });
      if (!r.ok) throw new Error("failed to load goals");
      return r.json();
    },
  });

  const { data: slipping } = useQuery<SlippingResponse>({
    queryKey: ["/api/rep-dashboard/slipping", userId, 7],
    queryFn: async () => {
      const r = await fetch(
        `/api/rep-dashboard/slipping?staleDays=7&limit=10${userId ? `&userId=${userId}` : ""}`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error("failed to load slipping leads");
      return r.json();
    },
  });

  const goalFor = (metricKey: string, fallback: number): number => {
    const g = goalsResp?.goals?.find((x) => x.metricKey === metricKey && x.period === "monthly");
    return g ? g.target : fallback;
  };

  const ringMetrics = useMemo(() => {
    if (!summary) return [];
    const m = summary.metrics;
    return [
      { key: "leads", value: m.leadsCreated.value, delta: m.leadsCreated.delta, label: "Leads", color: RING_COLORS.leads, goal: goalFor("leads", 20), icon: <Users className="w-4 h-4" /> },
      { key: "clients", value: m.clientsCreated.value, delta: m.clientsCreated.delta, label: "Clients", color: RING_COLORS.clients, goal: goalFor("clients", 5), icon: <UserPlus className="w-4 h-4" /> },
      { key: "dollars", value: m.dollarsBooked.value, delta: m.dollarsBooked.delta, label: "Booked", color: RING_COLORS.dollars, goal: goalFor("dollars_booked", 5000), icon: <DollarSign className="w-4 h-4" />, format: compactUsd },
      { key: "emails", value: m.emailsSent.value, delta: m.emailsSent.delta, label: "Emails", color: RING_COLORS.emails, goal: goalFor("emails", 50), icon: <Mail className="w-4 h-4" /> },
      { key: "calls", value: m.callsLogged.value, delta: m.callsLogged.delta, label: "Calls", color: RING_COLORS.calls, goal: goalFor("calls", 30), icon: <Phone className="w-4 h-4" /> },
    ];
  }, [summary, goalsResp]);

  const slippingLeads = slipping?.leads ?? [];

  return (
    <Shell>
      <Masthead label="My progress" />

      <section className="px-5 pt-3">
        <Eyebrow>This month</Eyebrow>
        <FitnessRingCard metrics={ringMetrics} isLoading={summaryLoading} />
      </section>

      <section className="px-5 pt-7">
        <Eyebrow>Quick access</Eyebrow>
        <TileGrid tiles={REP_TILES} />
      </section>

      <section className="px-5 pt-7">
        <Eyebrow>Follow-ups</Eyebrow>
        <FollowupsCard />
      </section>

      {slippingLeads.length > 0 && (
        <section className="px-5 pt-7">
          <Eyebrow>Slipping leads</Eyebrow>
          <div className="overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
            {slippingLeads.map((l) => (
              <Link key={l.id} href={`/leads?focus=${l.id}`}>
                <div className="flex items-center gap-3 px-4 py-3 active:bg-foreground/[0.03]" data-testid={`row-slipping-${l.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{l.leadName}</p>
                    <p className="truncate text-xs text-muted-foreground capitalize">{l.status?.replace(/_/g, " ")}</p>
                  </div>
                  <span className="shrink-0 text-xs font-medium text-muted-foreground/80">{l.daysSinceTouch}d cold</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </Shell>
  );
}

/* ── Marketing ────────────────────────────────────────────────────── */

const MARKETING_TILES: Tile[] = [
  { id: "comms", label: "Comms", icon: Mail, href: "/email-hub" },
  { id: "leads", label: "Leads", icon: Users, href: "/leads" },
  { id: "reports", label: "Reports", icon: BarChart3, href: "/reports" },
];

function MarketingMobile() {
  return (
    <Shell>
      <Masthead label="Marketing" />
      <section className="px-5 pt-3">
        <div className="rounded-3xl border border-border bg-card p-6">
          <Megaphone className="h-7 w-7 text-primary" />
          <h2 className="mt-4 text-xl font-semibold tracking-tight text-foreground">Your campaigns live here</h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Reach clients through email drips, SMS bursts, and the Lead Gen module — all from the
            Communications hub.
          </p>
          <Link href="/email-hub">
            <button
              className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground active:scale-[0.98] transition-transform"
              data-testid="button-open-comms"
            >
              Open Communications <ArrowUpRight className="h-4 w-4" />
            </button>
          </Link>
        </div>
      </section>
      <section className="px-5 pt-7">
        <Eyebrow>Quick access</Eyebrow>
        <TileGrid tiles={MARKETING_TILES} />
      </section>
    </Shell>
  );
}

/* ── Artist (dashboard route) ─────────────────────────────────────── */

const ARTIST_TILES: Tile[] = [
  { id: "home", label: "Backstage", icon: Sparkles, href: "/artist" },
  { id: "events", label: "Events", icon: CalendarDays, href: "/events" },
  { id: "schedule", label: "Schedule", icon: Music, href: "/schedule" },
  { id: "contacts", label: "Contacts", icon: Users, href: "/talent/contacts" },
  { id: "number", label: "My Number", icon: Phone, href: "/talent/my-number" },
  { id: "profile", label: "Profile", icon: Receipt, href: "/my-profile" },
];

function ArtistMobile() {
  return (
    <Shell>
      <Masthead label="Backstage" />
      <section className="px-5 pt-3">
        <Link href="/artist">
          <div
            className="rounded-3xl border border-border bg-card p-6 active:scale-[0.99] transition-transform"
            data-testid="hero-open-backstage"
          >
            <Sparkles className="h-7 w-7 text-primary" />
            <h2 className="mt-4 text-xl font-semibold tracking-tight text-foreground">Open your Backstage</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Your next show, call sheets, contacts, and earnings — tuned for the road.
            </p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
              Go to Backstage <ChevronRight className="h-4 w-4" />
            </span>
          </div>
        </Link>
      </section>
      <section className="px-5 pt-7">
        <Eyebrow>Quick access</Eyebrow>
        <TileGrid tiles={ARTIST_TILES} />
      </section>
    </Shell>
  );
}

/* ── Switch ───────────────────────────────────────────────────────── */

export default function BackstageDashboard({
  userRole,
  userId,
}: {
  userRole: UserRole;
  userId: string;
}) {
  switch (userRole) {
    case "admin":
      return <AdminMobile />;
    case "billing":
      return <BillingMobile />;
    case "marketing":
      return <MarketingMobile />;
    case "artist":
      return <ArtistMobile />;
    case "sales_rep":
    default:
      return <SalesRepMobile userId={userId} />;
  }
}
